/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import SelectField from "../../../Components/Masters/SelectField";
import countries from "../../../Pages/Masters/Roles and Permissions/Country.json";

const FUEL_TYPE_OPTIONS = [
  "Natural gas",
  "Coal",
  "Petroleum/Gasoline",
  "Diesel",
  "Fuel oil",
  "LPG",
  "Propane",
];

const FUEL_UNIT_BY_TYPE = {
  "Natural gas": ["M3"],
  Coal: ["Tonnes", "Kg"],
  "Petroleum/Gasoline": ["Liters"],
  Diesel: ["Liters"],
  "Fuel oil": ["Liters"],
  LPG: ["Liters", "Tonnes"],
  Propane: ["Kg", "Tonnes"],
};

const unitMenuProps = {
  PaperProps: {
    style: {
      maxHeight: "55%",
      overflowY: "auto",
    },
  },
};

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  leasedAssetOptions = [],
  onSearchLeasedAssets = () => {},
}) => {
  const isMongoId = (value) =>
    typeof value === "string" && /^[a-f\d]{24}$/i.test(value);

  const toDateInputValue = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

      const dmyMatch = value.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
      if (dmyMatch) {
        const dd = String(dmyMatch[1]).padStart(2, "0");
        const mm = String(dmyMatch[2]).padStart(2, "0");
        const yyyy = dmyMatch[3];
        return `${yyyy}-${mm}-${dd}`;
      }
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  // Safely extract assetSize value - handle both object and primitive values
  const getAssetSizeValue = () => {
    const assetSize = initialData?.assetSize;
    if (!assetSize) return "";
    if (typeof assetSize === "object" && assetSize !== null) {
      return assetSize?.value ?? "";
    }
    return assetSize ?? "";
  };

  const initialFormData = {
    assetMasterId: isMongoId(initialData?.assetId) ? initialData?.assetId : "",
    assetID: initialData?.assetID || initialData?.assetId || "",
    typeOfAsset: initialData?.typeOfAsset || initialData?.typeofAsset || "",
    leaseStartDate: toDateInputValue(initialData?.leaseStartDate),
    leaseEndDate: toDateInputValue(initialData?.leaseEndDate),
    assetSize: getAssetSizeValue(),
    electricityConsumption: initialData?.electricityConsumption || "",
    fuelType: initialData?.fuelType || "",
    fuelConsumption: initialData?.fuelConsumption || "",
    fuelConsumptionUnit: initialData?.fuelConsumptionUnit || "",
    remark: initialData?.remark || "",
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const [assetSearchText, setAssetSearchText] = useState("");

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
    setAssetSearchText(initialFormData.assetID || "");
  }, [isOpen, initialData]);

  const fieldConfig = [
    {
      label: "Asset ID",
      name: "assetID",
      type: "text",
      required: true,
      placeholder: "Enter Asset ID",
    },
    // {
    //   label: "Country",
    //   name: "country",
    //   type: "select",
    //   required: true,
    //   placeholder: "Enter country",
    // },
    // {
    //   label: "Location(City)",
    //   name: "location",
    //   type: "text",
    //   required: true,
    //   placeholder: "Enter location",
    // },
    {
      label: "Type of Asset",
      name: "typeOfAsset",
      type: "text",
      required: true,
      placeholder: "Enter type of asset",
      disabled: true,
    },
    {
      label: "Lease Start Date",
      name: "leaseStartDate",
      type: "date",
      required: true,
      disabled: true,
    },
    {
      label: "Lease End Date",
      name: "leaseEndDate",
      type: "date",
      required: true,
      disabled: true,
    },
    {
      label: "Asset Size (m² or unit count)",
      name: "assetSize",
      type: "text",
      required: true,
      placeholder: "Enter asset size",
      disabled: true,
    },
   
    {
      label: "Electricity Consumption (in KWH)",
      name: "electricityConsumption",
      type: "number",
      required: true,
      placeholder: "Enter electricity consumption",
    },
    {
      label: "Fuel Name",
      name: "fuelType",
      type: "select",
      required: true,
      options: FUEL_TYPE_OPTIONS,
    },
    {
      label: "Fuel Quantity",
      name: "fuelConsumption",
      type: "number",
      required: true,
      placeholder: "Enter consumption",
    },
    {
      label: "Fuel Unit",
      name: "fuelConsumptionUnit",
      type: "select",
      required: true,
      options: [],
    },
 
    {
      label: "Remark",
      name: "remark",
      type: "text",
      required: false,
      placeholder: "Enter remark",
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    fieldConfig.forEach((field) => {
      const value = formData[field?.name];

      if (field?.required) {
        if (value === "" || value === null || value === undefined) {
          newErrors[field?.name] = `${field?.label} is required.`;
          return;
        }
      }

      if (field?.type === "number" && value !== "" && isNaN(Number(value))) {
        newErrors[field?.name] = `${field?.label} must be a number.`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const applySelectedAsset = (selected) => {
    if (!selected) return;
    setFormData((prev) => ({
      ...prev,
      assetMasterId: selected._id || "",
      assetID: selected.assetId || "",
    
      typeOfAsset: selected.typeOfAsset || "",
      leaseStartDate: toDateInputValue(selected.leaseStartDate),
      leaseEndDate: toDateInputValue(selected.leaseEndDate),
      assetSize:
        selected.assetSize !== undefined && selected.assetSize !== null
          ? String(selected.assetSize)
          : "",
    }));
    setErrors((prev) => ({
      ...prev,
      assetID: "",
      typeOfAsset: "",
      leaseStartDate: "",
      leaseEndDate: "",
      assetSize: "",
    }));
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    if (type === "number" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    if (name === "assetID") {
      const selected = leasedAssetOptions.find((item) => item?._id === value);
      if (selected) {
        applySelectedAsset(selected);
        setAssetSearchText(selected?.assetId || "");
        return;
      }
    }

    if (name === "fuelType") {
      setFormData((prev) => ({
        ...prev,
        fuelType: value,
        fuelConsumptionUnit: "",
      }));
      setErrors((prev) => ({
        ...prev,
        fuelType: "",
        fuelConsumptionUnit: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;
    
    const formDataToSubmit = {
      ...formData,
      documents: uploadedFiles,
    };
    
    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => (
              <div key={field?.name} style={{ margin: "1rem 0" }}>
                {field?.type === "docs" ? (
                  <FileField
                    title={formatTitleWithOptional("Upload Documents (optional)")}
                    name={field?.name}
                    errors={errors[field?.name]}
                    fileUrls={uploadedFiles}
                    progress={uploadProgress[field?.name] || []}
                    {...{
                      setUploadedFiles,
                      setUploadProgress,
                      setErrors,
                    }}
                  />
                ) : field?.type === "select" && field?.name === "country" ? (
                  <SelectField
                    title={field?.label}
                    name={field?.name}
                    value={formData[field?.name]}
                    disable={true}
                    handleChange={(e) => {
                      const value = e.target.value === "Tap to select" ? "" : e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        [field?.name]: value,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        [field?.name]: "",
                      }));
                    }}
                    options={countries.countries.map((country) => country.name)}
                    errors={errors[field?.name]}
                  />
                ) : field?.type === "select" && field?.name === "fuelType" ? (
                  <SelectField
                    title={field?.label}
                    name={field?.name}
                    value={formData[field?.name]}
                    handleChange={handleChange(field?.name, field?.type)}
                    options={FUEL_TYPE_OPTIONS}
                    errors={errors[field?.name]}
                  />
                ) : field?.type === "select" &&
                  field?.name === "fuelConsumptionUnit" ? (
                  <></>
                ) : field?.name === "fuelConsumption" ? (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                      {field?.label}
                    </Typography>
                    <Box display="flex" gap={1} sx={{ width: "100%" }}>
                      <Box sx={{ width: "60%" }}>
                        <TextField
                          type="text"
                          size="small"
                          fullWidth
                          placeholder={field?.placeholder || ""}
                          value={formData.fuelConsumption || ""}
                          onChange={handleChange("fuelConsumption", "number")}
                          error={Boolean(errors.fuelConsumption)}
                          helperText={errors.fuelConsumption || ""}
                        />
                      </Box>
                      <Box sx={{ width: "40%" }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          name="fuelConsumptionUnit"
                          value={formData.fuelConsumptionUnit || ""}
                          onChange={handleChange("fuelConsumptionUnit", "select")}
                          disabled={!formData.fuelType}
                          error={Boolean(errors.fuelConsumptionUnit)}
                          SelectProps={{ MenuProps: unitMenuProps, displayEmpty: true }}
                        >
                          <MenuItem value="" disabled>
                            Unit
                          </MenuItem>
                          {(FUEL_UNIT_BY_TYPE[formData.fuelType] || []).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </TextField>
                        {errors.fuelConsumptionUnit && (
                          <Typography variant="body2" color="#D34040">
                            {errors.fuelConsumptionUnit}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ) : field?.name === "assetID" ? (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
                      {field?.label}
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={leasedAssetOptions}
                      value={
                        leasedAssetOptions.find(
                          (option) =>
                            option?._id === formData.assetMasterId ||
                            option?._id === formData.assetID ||
                            option?.assetId === formData.assetID
                        ) || null
                      }
                      inputValue={assetSearchText}
                      onInputChange={(event, newInputValue, reason) => {
                        setAssetSearchText(newInputValue);
                        if (reason === "input" || reason === "clear") {
                          onSearchLeasedAssets(newInputValue);
                        }
                      }}
                      onChange={(event, newValue) => {
                        applySelectedAsset(newValue);
                        setAssetSearchText(newValue?.assetId || "");
                      }}
                      getOptionLabel={(option) => option?.assetId || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Tap to Select"
                          error={Boolean(errors[field?.name])}
                          helperText={errors[field?.name] || ""}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-inputRoot": {
                          height: 40,
                          alignItems: "center",
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field?.label}
                    </Typography>

                    <TextField
                      type={field?.type === "date" ? "date" : "text"}
                      size="small"
                      fullWidth
                      required={field?.required}
                      multiline={field?.name === "remark"}
                      minRows={field?.name === "remark" ? 3 : undefined}
                      placeholder={field?.placeholder || ""}
                      value={formData[field?.name] || ""}
                      onChange={handleChange(field?.name, field?.type)}
                      disabled={Boolean(field?.disabled)}
                      error={Boolean(errors[field?.name])}
                      helperText={errors[field?.name] || ""}
                      sx={{
                        ...(field?.disabled && {
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#6b7280",
                            cursor: "not-allowed",
                          },
                          "& .MuiInputBase-root.Mui-disabled": {
                            backgroundColor: "#f3f4f6",
                          },
                        }),
                      }}
                      inputProps={
                        field?.type === "date"
                          ? { max: new Date().toISOString().split("T")[0] }
                          : {}
                      }
                    />
                  </>
                )}
              </div>
            ))}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;
